// =======================
// src/paginas/adminPage/modalidades/CriarModalidade.js
// =======================

import React, { useState } from "react";

import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
} from "@mui/material";

import { styled } from "@mui/system";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

import ErrorState from "@componentes/ErrorWindow";
import { api } from "@api/api";

import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";
import { ajustarTamanhoFonteSre } from "../../../estilos/global";

// Container principal com gradiente e sombra.
const GradientPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: "24px",
  background: "linear-gradient(145deg, #ffffff, #f5f7ff)",
  boxShadow: "0 20px 50px rgba(63, 81, 181, 0.15)",
  maxWidth: "600px",
  margin: "auto",
  marginTop: "5%",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: "linear-gradient(90deg, #3f51b5, #2196f3)",
  },
}));

// Botão com efeito de onda ao clicar.
const RippleButton = styled(Button)(() => ({
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::after": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "5px",
    height: "5px",
    background: "rgba(255, 255, 255, 0.5)",
    opacity: 0,
    borderRadius: "100%",
    transform: "scale(1, 1) translate(-50%)",
    transformOrigin: "50% 50%",
  },

  "&:active::after": {
    animation: "ripple 0.6s ease-out",
  },

  "@keyframes ripple": {
    "0%": {
      transform: "scale(0, 0) translate(-50%)",
      opacity: 1,
    },

    "100%": {
      transform: "scale(20, 20) translate(-50%)",
      opacity: 0,
    },
  },
}));

const CadastroModalidadePage = () => {
  const [nomeModalidade, setNomeModalidade] = useState("");
  const [isEnsinoInfantil, setIsEnsinoInfantil] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [errorType, setErrorType] = useState(null);

  const handleNomeChange = (event) => {
    setNomeModalidade(event.target.value);
  };

  const handleCheckboxChange = (event) => {
    setIsEnsinoInfantil(event.target.checked);
  };

  const handleSubmit = async () => {
    if (!nomeModalidade.trim()) return;

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        api.adminCreateModality(),
        {
          nome: nomeModalidade,
          ensino_infantil: isEnsinoInfantil,
        },
        {
          withCredentials: true,
        },
      );

      console.log("Modalidade criada com sucesso:", response.data);
      navigate(-1);
    } catch (error) {
      console.error("Erro ao criar modalidade:", error);

      if (error.message.includes("Network Error")) {
        setErrorType("network");
      } else if (error.response?.status === 403) {
        setErrorType("permission");
      } else {
        setErrorType("module");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (errorType) {
    return (
      <ErrorState
        errorType={errorType}
        onRetry={() => {
          setErrorType(null);
        }}
      />
    );
  }

  return (
    <TransicaoPagina variante="navegacao">
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(135deg, #f5f7ff 0%, #f0f4ff 100%)",
          p: 3,
        }}
      >
        <GradientPaper elevation={3}>
          {/* Botão Voltar */}
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            style={{
              position: "absolute",
              top: 20,
              left: 20,
            }}
          >
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                backgroundColor: "rgba(63, 81, 181, 0.1)",

                "&:hover": {
                  backgroundColor: "rgba(63, 81, 181, 0.2)",
                },
              }}
            >
              <ArrowBackIcon sx={{ color: "#3f51b5" }} />
            </IconButton>
          </motion.div>

          {/* Título */}
          <Typography
            variant="h3"
            mb={4}
            sx={{
              fontWeight: 800,
              background: "linear-gradient(90deg, #3f51b5, #2196f3)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              letterSpacing: "-0.5px",
            }}
          >
            Nova Modalidade
          </Typography>

          {/* Formulário */}
          <Box component="form" sx={{ mt: 3 }}>
            <div>
              <TextField
                label="Nome da Modalidade"
                variant="outlined"
                fullWidth
                value={nomeModalidade}
                onChange={handleNomeChange}
                sx={{
                  marginBottom: 3,

                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",

                    "& fieldset": {
                      borderColor: "rgba(63, 81, 181, 0.2)",
                    },

                    "&:hover fieldset": {
                      borderColor: "rgba(63, 81, 181, 0.4)",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#3f51b5",
                      boxShadow: "0 0 0 2px rgba(63, 81, 181, 0.2)",
                    },
                  },

                  "& .MuiInputLabel-root": {
                    color: "rgba(63, 81, 181, 0.7)",
                  },
                }}
              />
            </div>

            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isEnsinoInfantil}
                    onChange={handleCheckboxChange}
                    sx={{
                      color: "#3f51b5",

                      "&.Mui-checked": {
                        color: "#3f51b5",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ color: "text.secondary" }}>
                    Educação Infantil
                  </Typography>
                }
                sx={{
                  marginBottom: 4,
                  padding: 1,
                  borderRadius: "8px",
                  backgroundColor: isEnsinoInfantil
                    ? "rgba(255, 152, 0, 0.08)"
                    : "transparent",
                  transition: "all 0.3s ease",
                }}
              />
            </div>

            <div>
              <RippleButton
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={!nomeModalidade.trim() || isSubmitting}
                sx={{
                  borderRadius: "12px",
                  padding: "14px",
                  fontSize: ajustarTamanhoFonteSre("16px"),
                  fontWeight: 600,
                  background: "linear-gradient(90deg, #3f51b5, #2196f3)",
                  color: "#ffffff",
                  boxShadow: "0 8px 20px rgba(63, 81, 181, 0.3)",

                  "&:hover": {
                    boxShadow: "0 12px 24px rgba(63, 81, 181, 0.4)",
                    transform: "translateY(-2px)",
                  },

                  "&:disabled": {
                    background: "rgba(0, 0, 0, 0.12)",
                    color: "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                {isSubmitting ? "Salvando..." : "Criar Modalidade"}
              </RippleButton>
            </div>
          </Box>
        </GradientPaper>
      </Box>
    </TransicaoPagina>
  );
};

export default CadastroModalidadePage;
